import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";

import DeleteDialog from "@/components/v2/molecules/admin/tabs/shared/DeleteDialog";
import { useIncomeByBookingId } from "@/hooks/admin/shared/useIncomeByBookingId";
import { useToast } from "@/hooks/use-toast";
import { Booking } from "@/hooks/useBookings";
import { parseDateFromDatabase } from "@/utils/dateParser";
import { copyUrlToClipboard } from "@/utils/urlUtils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Copy, ExternalLink, Link as LinkIcon, Pencil, Phone } from "lucide-react";
import { formatCurrency } from "@/utils/shared/currencyUtils";

function formatDateRange(checkIn: string, checkOut: string, nights: number): string {
  const checkInDate = parseDateFromDatabase(checkIn);
  const checkOutDate = parseDateFromDatabase(checkOut);
  
  if (!checkInDate || !checkOutDate) return "";
  
  const checkInFormatted = format(checkInDate, "d 'de' MMM", { locale: es });
  const checkOutFormatted = format(checkOutDate, "d 'de' MMM", { locale: es });
  
  return `${checkInFormatted} – ${checkOutFormatted} (${nights} ${nights === 1 ? 'noche' : 'noches'})`;
}

function formatFechaES(fechaISO: string): string {
  const date = parseDateFromDatabase(fechaISO);
  return date ? format(date, "dd/MMM/yyyy", { locale: es }) : fechaISO;
}

interface ReservationDetailModalProps {
  booking: Booking | null;
  isOpen: boolean;
  onClose: () => void;
  onCancelBooking: (bookingId: string) => void;
  onEdit: (booking: Booking) => void;
  isCanceling?: boolean;
  canEditReservation?: boolean;
  canCancelReservation?: boolean;
}

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" => {
  if (status === "Reservada") return "default";
  if (status === "Cancelada") return "destructive";
  return "secondary";
};

const getPaymentStatusVariant = (paymentStatus: string): "default" | "secondary" | "destructive" => {
  if (paymentStatus === "paid") return "default";
  if (paymentStatus === "partial") return "secondary";
  return "destructive";
};

const getPaymentStatusLabel = (paymentStatus: string): string => {
  if (paymentStatus === "paid") return "Pagado";
  if (paymentStatus === "partial") return "Pago parcial";
  return "Pendiente";
};

const ReservationDetailModal = ({
  booking,
  isOpen,
  onClose,
  onCancelBooking,
  onEdit,
  isCanceling,
  canEditReservation = true,
  canCancelReservation = true,
}: ReservationDetailModalProps) => {
  const { toast } = useToast();
  const { data: bookingIncomes = [], isLoading: isLoadingIncomes } = useIncomeByBookingId(booking?.booking_id || "");
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  if (!isOpen || !booking) return null;

  const welcomeUrl = `${window.location.origin}/reservacion/${booking.booking_id}`;

  const handleCopy = async () => {
    const result = await copyUrlToClipboard(welcomeUrl);
    toast({
      description: result
        ? "¡Enlace copiado al portapapeles!"
        : "Error al copiar el enlace",
      variant: result ? "default" : "destructive",
    });
  };

  const handleCopyReservation = () => {
    const reservationText = [
      booking.property_name || booking.property_short_name,
      booking.guest_name,
      formatDateRange(booking.check_in, booking.check_out, booking.nights),
      `${booking.total_guests} ${booking.total_guests === 1 ? 'huésped' : 'huéspedes'}`
    ].join('\n');
    
    navigator.clipboard.writeText(reservationText);
    toast({
      description: "Reservación copiada al portapapeles",
    });
  };

  const totalIncome = bookingIncomes.reduce(
    (sum, income) => sum + income.payout,
    0,
  );

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    onCancelBooking(booking.id);
    setShowCancelDialog(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="p-0 max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="text-2xl font-semibold">
              Detalles de la reservación
            </DialogTitle>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 pt-2 pb-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Hero Section */}
            <div className="space-y-0.5">
              {/* Status Badges - PRIMERO */}
              <div className="flex gap-2 pb-1.5">
                <Badge variant={getStatusVariant(booking.status)} className="rounded-full">
                  {booking.status}
                </Badge>
                <Badge variant={getPaymentStatusVariant(booking.payment_status)} className="rounded-full">
                  {getPaymentStatusLabel(booking.payment_status)}
                </Badge>
              </div>

              {/* Booking ID */}
              <p className="text-sm text-muted-foreground font-medium">
                {booking.booking_id}
              </p>
              
              {/* Origin · Property · Estimated Payout */}
              <p className="text-sm text-muted-foreground">
                {booking.origin_name || booking.origins?.name || "Sin origen"} · {booking.property_short_name}
                {booking.estimated_payout !== null && booking.estimated_payout !== undefined && (
                  <> · {formatCurrency(booking.estimated_payout)}</>
                )}
              </p>
              
              {/* Property Name */}
              {booking.property_name && (
                <p className="text-sm text-muted-foreground">
                  {booking.property_name}
                </p>
              )}
              
          {/* Guest Name con enlace a página de reservación */}
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold">{booking.guest_name}</h3>
            <a
              href={welcomeUrl}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Ver página de reservación"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
              
              {/* Dates */}
              <p className="text-sm text-muted-foreground">
                {formatDateRange(booking.check_in, booking.check_out, booking.nights)}
              </p>
              
              {/* Guests */}
              <p className="text-sm text-muted-foreground">
                {booking.total_guests} {booking.total_guests === 1 ? 'huésped' : 'huéspedes'}
              </p>
              
              {/* Phone */}
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{booking.guest_phone || "Sin teléfono"}</span>
              </div>
            </div>

            {/* Action Buttons - Nueva distribución */}
            <div className="space-y-2 pt-3">
              {/* Fila 1: Editar + Copiar reservación + Copiar URL */}
              <div className="flex gap-2">
                {canEditReservation && (
                  <Button
                    variant="outline"
                    className="flex-1 h-12 rounded-xl border-foreground/80 hover:bg-muted text-foreground font-medium"
                    onClick={() => onEdit(booking)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Editar reservación
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-xl border-foreground/80 hover:bg-muted text-foreground font-medium"
                  onClick={handleCopyReservation}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copiar reservación
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 rounded-xl border-foreground/80 hover:bg-muted text-foreground font-medium"
                  onClick={handleCopy}
                >
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Copiar URL
                </Button>
              </div>
              
              {/* Fila 2: Cancelar reservación (100%) */}
              {canCancelReservation && booking.status !== "Cancelada" && (
                <Button
                  variant="outline"
                  className="w-full h-12 rounded-xl border-foreground/80 hover:bg-muted text-foreground font-medium"
                  onClick={handleCancelClick}
                  disabled={isCanceling}
                >
                  {isCanceling ? "Cancelando..." : "Cancelar reservación"}
                </Button>
              )}
            </div>

            <Separator />

            {/* Notes Section - Conditional */}
            {booking.note?.trim() && (
              <>
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Nota</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {booking.note}
                  </p>
                </div>
                <Separator />
              </>
            )}

            {/* Payments Section */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Pagos registrados</h4>
              
              {bookingIncomes.length > 0 ? (
                <div className="space-y-4">
                  {bookingIncomes.map((item) => (
                    <div key={item.id} className="space-y-0.5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{item.income_type}</span>
                        <span className="text-sm">{item.payment_type}</span>
                        <span className="text-sm font-medium text-right">
                          {formatCurrency(item.payout)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(parseDateFromDatabase(item.payout_date), "d 'de' MMM yyyy", { locale: es })}
                      </p>
                    </div>
                  ))}
                  
                  {/* Total */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Total</span>
                      <span className="text-sm font-semibold text-success">
                        {formatCurrency(totalIncome)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Sin pagos registrados</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {canCancelReservation && (
        <DeleteDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          onConfirm={handleConfirmCancel}
          title="¿Cancelar reservación?"
          confirmText="Sí, cancelar reservación"
          cancelText="No, mantener reservación"
          confirmClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          description={
            <>
              Esta acción cancelará la reservación de{" "}
              <strong>{booking.guest_name}</strong> para las fechas{" "}
              <strong>{formatFechaES(booking.check_in)}</strong> a{" "}
              <strong>{formatFechaES(booking.check_out)}</strong>.
              <br />
              <br />
              Esta acción no se puede deshacer.
            </>
          }
        />
      )}
    </>
  );
};

export default ReservationDetailModal;
